import * as React from "react";
import { Password } from "./PasswordBase";
import { ReactComponent as CheckCircle } from "@built/design-system/icons/action/check_circle.svg";
import l10n from "@built/i18n";

import * as styles from "./PasswordField.module.scss";

/**
 * Renders a password field with a password strength indicator
 */
export default function PasswordField(props: PasswordFieldProps) {
  const { className, passwordStrength } = props;

  return (
    <div className={className}>
      <Password
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        autoComplete="off"
        label={l10n.t("PasswordField:info.label", "Password")}
        name="password"
        onChange={props.onChange}
        value={props.password}
        size="lg"
      />

      <ul className={[styles.rules, styles.bullets].join(" ")}>
        <li
          className={
            passwordStrength && passwordStrength.longEnough
              ? styles.satisfied
              : undefined
          }
        >
          <CheckCircle
            className={styles.strengthIndicatorIcon}
            width={14}
            height={14}
          />
          {l10n.t("PasswordField:info.length", "At least 10 characters")}
        </li>
        <li
          className={
            passwordStrength && passwordStrength.hasLowercase
              ? styles.satisfied
              : undefined
          }
        >
          <CheckCircle
            className={styles.strengthIndicatorIcon}
            width={14}
            height={14}
          />
          {l10n.t(
            "PasswordField:info.lowercase",
            "At least one lowercase character"
          )}
        </li>
        <li
          className={
            passwordStrength && passwordStrength.hasNumbers
              ? styles.satisfied
              : undefined
          }
        >
          <CheckCircle
            className={styles.strengthIndicatorIcon}
            width={14}
            height={14}
          />
          {l10n.t("PasswordField:info.number", "At least one number")}
        </li>
      </ul>
    </div>
  );
}

export interface PasswordFieldProps {
  /** An additional class name to provide custom styling */
  className?: string;

  /** Whether the input should be disabled */
  disabled?: boolean;

  /** Whether the input should be focused as soon as it is rendered */
  autoFocus?: boolean;

  /** The current password value to render in the component */
  password: string;

  /** Whether the currently entered password is strong enough */
  passwordStrength?: PasswordStrength;

  /** Callback to handle updates to the current password value */
  onChange(event: React.SyntheticEvent<HTMLInputElement>): void;
}

export interface PasswordStrength {
  longEnough: boolean;
  hasLowercase: boolean;
  hasNumbers: boolean;
}
